import { Controller } from "@hotwired/stimulus"
import { show_success, showBlock } from "lib/helpers"

// chat/bots/create — data-controller="chat--bots-create"
// Create AI Agent form (assistants/new).
export default class extends Controller {
  static targets = [
    "form",
    "commandInput",
    "commandExample",
    "avatarInput",
    "avatarImage",
    "systemMessageToggle",
    "systemMessageGroup",
  ]

  connect() {
    this.commandDebounceTimeout = null
    this.copyFeedbackTimeout = null

    const $root = $(this.element)

    if (this.hasFormTarget) this.#bindForm()
    $root.find("#bot_ai_model_name_select").on("change.chatBotsCreate", this.modelSelectChanged)
    if (this.hasCommandInputTarget) {
      $(this.commandInputTarget).on("input.chatBotsCreate", this.commandInputChanged)
    }
    $root
      .find('input[name="chat_assistant[forward_strategy]"]')
      .on("change.chatBotsCreate", this.forwardStrategyChanged)
    const $kbRadios = $root.find('input[name="chat_assistant[is_knowledge_base_enabled]"]')
    $kbRadios.on("change.chatBotsCreate", this.knowledgeBaseChanged)
    $kbRadios.filter(":checked").trigger("change")
    $root.find("textarea").each((_i, textarea) => {
      const updateCounter = () => {
        $(textarea).parent().find(".textaria-counter span").html($(textarea).val().length)
      }
      $(textarea).on("change.chatBotsCreate paste.chatBotsCreate keyup.chatBotsCreate", updateCounter)
      updateCounter()
    })
    if (this.hasAvatarInputTarget) {
      $(this.avatarInputTarget).on("change.chatBotsCreate", this.avatarChanged)
    }
  }

  disconnect() {
    if (this.commandDebounceTimeout) clearTimeout(this.commandDebounceTimeout)
    this.commandDebounceTimeout = null
    if (this.copyFeedbackTimeout) clearTimeout(this.copyFeedbackTimeout)
    this.copyFeedbackTimeout = null

    if (this.hasFormTarget) $(this.formTarget).ajaxFormUnbind()

    const $root = $(this.element)
    $root.find("#bot_ai_model_name_select").off(".chatBotsCreate")
    if (this.hasCommandInputTarget) $(this.commandInputTarget).off(".chatBotsCreate")
    $root.find('input[name="chat_assistant[forward_strategy]"]').off(".chatBotsCreate")
    $root.find('input[name="chat_assistant[is_knowledge_base_enabled]"]').off(".chatBotsCreate")
    $root.find("textarea").off(".chatBotsCreate")
    if (this.hasAvatarInputTarget) $(this.avatarInputTarget).off(".chatBotsCreate")
    $(".avatar-uploaded-image").off(".chatBotsCreate")
    $(".reload-avatar").off(".chatBotsCreate")
  }

  hideAlert(event) {
    event.preventDefault()
    const alert = event.currentTarget.closest(".alert-blue")
    if (alert) alert.style.display = "none"
  }

  copySigningSecret(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const textToCopy = $(trigger).siblings(".copy-field").val()

    const tempTextArea = document.createElement("textarea")
    tempTextArea.value = textToCopy
    document.body.appendChild(tempTextArea)
    tempTextArea.select()
    document.execCommand("copy")
    document.body.removeChild(tempTextArea)

    if (document.queryCommandSupported("copy")) {
      $(trigger).addClass("copy-success")
    } else {
      $(trigger).addClass("copy-error")
    }

    if (this.copyFeedbackTimeout) clearTimeout(this.copyFeedbackTimeout)
    this.copyFeedbackTimeout = setTimeout(() => {
      $(trigger).removeClass("copy-success copy-error")
    }, 2000)
  }

  toggleSystemMessage(event) {
    event.preventDefault()
    const toggle = event.currentTarget
    const isOpen = $(toggle).data("open")

    if (isOpen === false) {
      $(toggle).data("open", true).text("Hide")
    } else {
      $(toggle).data("open", false).text("Show")
    }

    if (this.hasSystemMessageGroupTarget) {
      $(this.systemMessageGroupTarget).fadeToggle("slow", "linear").removeClass("hide")
    }
  }

  avatarChanged = (event) => {
    if (event.currentTarget.disabled) {
      showBlock(this.#errorBlockSelector, "File upload not supported!")
      return
    }
    const files = event.currentTarget.files
    if (files && files[0]) this.#readImage(files[0])
  }

  modelSelectChanged = () => {
    const select = this.element.querySelector("#bot_ai_model_name_select")
    if (!select) return
    const maxTokens = $(select).find("option:selected").data("max-tokens")
    if (maxTokens === undefined) {
      $(".max_tokens_limit").html("Select model type to identify values range")
    } else {
      $(".max_tokens_limit").html(`Allowed range: 1 - ${maxTokens}`)
    }
  }

  commandInputChanged = () => {
    clearTimeout(this.commandDebounceTimeout)
    this.commandDebounceTimeout = setTimeout(() => {
      this.#sanitizeCommandInput($(this.commandInputTarget))
    }, 300)
  }

  forwardStrategyChanged = () => {
    const radio = $(this.element).find('input[name="chat_assistant[forward_strategy]"]:checked')
    const radioSection = $(`.control-block.${radio.val()}`)
    $(".show-control-block")
      .addClass("hide-control-block")
      .removeClass("show-control-block")
    radioSection.addClass("show-control-block").removeClass("hide-control-block")

    if (radio.val() === "none") {
      $(".help-block.short-summary").hide()
    } else {
      $(".help-block.short-summary").removeClass("hide").show()
    }
  }

  knowledgeBaseChanged = () => {
    const radio = $(this.element).find(
      'input[name="chat_assistant[is_knowledge_base_enabled]"]:checked',
    )
    if (radio.val() === "true") {
      this.#enableKnowledgeBaseControlGroup()
    } else {
      this.#disableKnowledgeBaseControlGroup()
    }
  }

  get #errorBlockSelector() {
    return "#chatbot-errors"
  }

  #bindForm() {
    $(this.formTarget).ajaxForm({
      dataType: "json",
      success: (response) => {
        if (response.success) {
          show_success(response.message)
          window.location.href = response.path
        } else {
          const error = $(this.#errorBlockSelector)
          if (error.length === 1) error.after(error.clone())
          $.scrollTo("body", "slow")
          showBlock(this.#errorBlockSelector, response.message)
        }
        $(this.formTarget).find("input[type='submit']").enable(true)
      },
      error: () => {
        $.scrollTo("body", "slow")
        showBlock(this.#errorBlockSelector, "An error occurred during request")
        $(this.formTarget).find("input[type='submit']").enable(true)
      },
    })
  }

  #sanitizeCommandInput($element) {
    const value = $element.val()
    const sanitizedValue = this.#sanitizeCommand(value)

    if (value !== sanitizedValue) $element.val(sanitizedValue)

    if (this.hasCommandExampleTarget) {
      this.commandExampleTarget.textContent = `/${sanitizedValue}`
    } else {
      $(".chatbot-command-example").html(`/${sanitizedValue}`)
    }
  }

  #sanitizeCommand(input) {
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+\s*=/gi, "")
      .replace(/<iframe/gi, "")
      .replace(/<object/gi, "")
      .replace(/<embed/gi, "")
      .replace(/[<>\"'&]/g, "")
  }

  #enableKnowledgeBaseControlGroup() {
    $(".knowledge-base-control-group").removeAttr("disabled")
    $(".knowledge-base-control-group .tooltip-ico-disabled").hide()
    $(".knowledge-base-control-group .tooltip-ico").show()
  }

  #disableKnowledgeBaseControlGroup() {
    $(".knowledge-base-control-group").attr("disabled", "disabled")
    $(".knowledge-base-control-group .tooltip-ico").hide()
    $(".knowledge-base-control-group .tooltip-ico-disabled").show()
  }

  #readImage(file) {
    if (file.size > 1048576) {
      showBlock(
        this.#errorBlockSelector,
        `Image is too large (current: ${file.size} Byte, max: 1MB (1048576)`,
      )
      if (this.hasAvatarInputTarget) $(this.avatarInputTarget).val("")
      return
    }

    const reader = new FileReader()
    const image = new Image()

    reader.readAsDataURL(file)
    reader.onload = (_file) => {
      image.src = _file.target.result
      image.onload = () => {
        $(".avatar-uploaded-image").removeClass("hide")
        if (this.hasAvatarImageTarget) {
          $(this.avatarImageTarget).attr("src", image.src)
        } else {
          $("img#current-avatar").attr("src", image.src)
        }
        this.#bindHoverAvatar()
      }
      image.onerror = () => {
        showBlock(this.#errorBlockSelector, `Invalid file type: ${file.type}`)
        if (this.hasAvatarInputTarget) $(this.avatarInputTarget).val("")
      }
    }
  }

  #bindHoverAvatar() {
    $(".avatar-uploaded-image").on("mouseenter.chatBotsCreate", () => {
      $(".reload-avatar").removeClass("hide")
      $(".avatar-uploaded-image").off("mouseenter.chatBotsCreate")
      this.#bindRemoveAvatar()
    })
  }

  #bindRemoveAvatar() {
    $(".reload-avatar")
      .on("mouseleave.chatBotsCreate", () => {
        $(".reload-avatar").addClass("hide").off("mouseleave.chatBotsCreate click.chatBotsCreate")
        this.#bindHoverAvatar()
      })
      .on("click.chatBotsCreate", () => {
        const defaultUrl = this.hasAvatarImageTarget
          ? $(this.avatarImageTarget).data("default-url")
          : $("img#current-avatar").data("default-url")
        if (this.hasAvatarImageTarget) {
          $(this.avatarImageTarget).attr("src", defaultUrl)
        } else {
          $("img#current-avatar").attr("src", defaultUrl)
        }
        if (this.hasAvatarInputTarget) $(this.avatarInputTarget).val("")
        $(".reload-avatar").off("mouseleave.chatBotsCreate click.chatBotsCreate")
        $(".avatar-uploaded-image").off("mouseenter.chatBotsCreate").addClass("hide")
        $(".reload-avatar").addClass("hide")
      })
  }
}
